import React from 'react';
import {
  ArrowRight,
  Package,
  MapPin,
  Calendar,
  User,
  Hash,
  Barcode
} from 'lucide-react';
import { POrderHistoryTransactionDto } from '../../../../types/purchaseOrderHistory';
import { purchaseOrderHistoryService } from '../../../../services/purchaseOrderHistoryService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { StatusBadge } from '../../../common/StatusBadge';

interface TransactionRowProps {
  transaction: POrderHistoryTransactionDto;
}

export const TransactionRow: React.FC<TransactionRowProps> = ({ transaction }) => {
  const { t } = useTranslation();

  // Get background color based on line type
  const getLineTypeBadgeClass = (lineType: number): string => {
    switch (lineType) {
      case 0: // Varemottak
        return 'bg-green-100 text-green-800';
      case 1: // Lagertelling (fri)
        return 'bg-yellow-100 text-yellow-800';
      case 2: // Lageroverføring
        return 'bg-blue-100 text-blue-800';
      case 3: // Lagerkorreksjon
        return 'bg-orange-100 text-orange-800';
      case 4: // Lagertelling (forhåndsdefinert)
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const hasLocation = transaction.locationName || transaction.toLocationName;
  const isTransfer = transaction.locationName && transaction.toLocationName;

  return (
    <div className="px-3 py-2 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between gap-3">
        {/* Left side - Type and Status */}
        <div className="flex items-start space-x-2 min-w-[140px]">
          <div className="flex flex-col gap-1">
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded ${getLineTypeBadgeClass(
                transaction.lineType
              )}`}
            >
              {transaction.lineTypeText}
            </span>
            {/* LineStatus resolved live against master StatusCodes (StatusType 1), shown as "kode - tekst". */}
            <StatusBadge value={transaction.lineStatus} statusType={1} />
          </div>
        </div>

        {/* Center - Transaction Details */}
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {/* Quantity */}
            <div className="flex items-center text-gray-700">
              <Package className="mr-1.5 text-gray-500 flex-shrink-0" size={12} />
              <span className="font-semibold">{transaction.quantity || 0}</span>
              {transaction.itemUnitCode && (
                <span className="ml-1 text-gray-500">{transaction.itemUnitCode}</span>
              )}
            </div>

            {/* Location */}
            {hasLocation && (
              <div className="flex items-center text-gray-700">
                <MapPin className="mr-1.5 text-gray-500 flex-shrink-0" size={12} />
                {isTransfer ? (
                  <span>
                    {transaction.locationName}
                    <ArrowRight className="inline mx-1" size={10} />
                    {transaction.toLocationName}
                  </span>
                ) : (
                  <span>{transaction.locationName || transaction.toLocationName}</span>
                )}
              </div>
            )}

            {/* Batch Number */}
            {transaction.itemBatchNo && (
              <div className="flex items-center text-gray-700">
                <Hash className="mr-1.5 text-gray-500 flex-shrink-0" size={12} />
                <span className="truncate">{t('manager.modules.purchaseOrderHistory.lineRow.batch', { '0': transaction.itemBatchNo }, `Batch: ${transaction.itemBatchNo}`)}</span>
                {transaction.itemBatchExpDate && (
                  <span className="ml-1 text-gray-500">
                    {t('manager.modules.purchaseOrderHistory.transactionRow.expiry', { '0': purchaseOrderHistoryService.formatDate(transaction.itemBatchExpDate) }, `(Utløp: ${purchaseOrderHistoryService.formatDate(transaction.itemBatchExpDate)})`)}
                  </span>
                )}
              </div>
            )}

            {/* Serial Number */}
            {transaction.itemSerialNo && (
              <div className="flex items-center text-gray-700">
                <Hash className="mr-1.5 text-gray-500 flex-shrink-0" size={12} />
                <span className="truncate">{t('manager.modules.purchaseOrderHistory.transactionRow.serialNo', { '0': transaction.itemSerialNo }, `SN: ${transaction.itemSerialNo}`)}</span>
              </div>
            )}

            {/* SSCC */}
            {transaction.sscc && (
              <div className="flex items-center text-gray-700">
                <Barcode className="mr-1.5 text-gray-500 flex-shrink-0" size={12} />
                <span className="truncate">{t('manager.modules.purchaseOrderHistory.transactionRow.sscc', { '0': transaction.sscc }, `SSCC: ${transaction.sscc}`)}</span>
              </div>
            )}

            {/* Operator */}
            {transaction.operatorID && (
              <div className="flex items-center text-gray-700">
                <User className="mr-1.5 text-gray-500 flex-shrink-0" size={12} />
                <span className="truncate">{transaction.operatorID}</span>
              </div>
            )}

            {/* Transaction Date */}
            {transaction.transDt && (
              <div className="flex items-center text-gray-700">
                <Calendar className="mr-1.5 text-gray-500 flex-shrink-0" size={12} />
                <span>{purchaseOrderHistoryService.formatDateTimeShort(transaction.transDt)}</span>
              </div>
            )}

            {/* Warehouse */}
            {transaction.warehouse && (
              <div className="flex items-center text-gray-700">
                <Package className="mr-1.5 text-gray-500 flex-shrink-0" size={12} />
                <span className="truncate">{transaction.warehouse}</span>
              </div>
            )}
          </div>

          {/* Item Info (if different from line) */}
          {transaction.description1 && (
            <div className="mt-1 text-xs text-gray-600 truncate">
              {transaction.description1}
            </div>
          )}
        </div>

        {/* Right side - Transaction ID */}
        <div className="text-right">
          <p className="text-xs text-gray-500">{t('manager.modules.purchaseOrderHistory.transactionRow.transId', {}, 'Trans ID')}</p>
          <p className="text-xs font-mono text-gray-700">{transaction.id}</p>
        </div>
      </div>
    </div>
  );
};
